<template>
  <OnBoardingFrame
    as-form
    back
    :step="5"
    :title="$t('on_boarding.profile_username_page.title')"
    :subtitle="$t('on_boarding.profile_username_page.subtitle')"
    :error="errorMessage"
    @back="backTapped"
    @submit="submit"
  >
    <!-- Step 5. Mirrors the mobile Username screen (iOS
         OnBoardingUsernameViewController / Android OnboardingUserNameFragment).

         Mobile generates its suggestions by calling OpenAI from the client. That
         doesn't port to the browser — a key shipped to the page is a public key —
         so web composes them locally from the same hiking word list. Same shapes,
         no network, no key. They're labelled plainly as suggestions rather than
         borrowing mobile's "HAILEY" AI branding, since no AI is involved. -->
    <div class="ob-subblock">
      <input
        id="profileUsername"
        ref="usernameInput"
        :value="username"
        type="text"
        class="ob-input"
        :placeholder="$t('on_boarding.profile_username_page.placeholder')"
        :aria-label="$t('on_boarding.profile_username_page.placeholder')"
        autocomplete="nickname"
        :maxlength="maxLength"
        required
        @input="onInput"
      >
      <p v-if="inlineError" class="ob-hint-error">{{ inlineError }}</p>
    </div>

    <div v-if="suggestions.length > 0" class="ob-subblock">
      <div class="ob-caps">{{ $t('on_boarding.profile_username_page.suggestions_label') }}</div>
      <div class="ob-chips">
        <button
          v-for="suggestion in suggestions"
          :key="suggestion"
          type="button"
          class="ob-chip suggestion-chip"
          :class="{ 'ob-chip--on suggestion-chip--active': suggestion === username }"
          :aria-pressed="suggestion === username"
          @click="pick(suggestion)"
        >
          {{ suggestion }}
        </button>
        <button type="button" class="ob-chip suggestion-chip suggestion-chip--more" @click="regenerate">
          <span class="ms" aria-hidden="true">refresh</span>
          <span>{{ $t('on_boarding.profile_username_page.regenerate') }}</span>
        </button>
      </div>
    </div>

    <template #footer>
      <button type="submit" class="ob-btn ob-btn-primary" :disabled="isLoading || !canSubmit">
        <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        <span>{{ $t('on_boarding.profile_username_page.continue') }}</span>
      </button>
    </template>
  </OnBoardingFrame>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import OnBoardingFrame from './OnBoardingFrame.vue';
import {
  normalizeUsername,
  validateUsername,
  suggestUsernames,
  composeFullName,
  USERNAME_MAX_LENGTH,
  USERNAME_MIN_LENGTH,
} from '../../../utils/helpers/signupProfile';

export default defineComponent({
  name: 'OnBoardingProfileUsernamePage',
  components: { OnBoardingFrame },
  data() {
    return {
      username: this.$store.state.onBoarding.profileUsername || '',
      maxLength: USERNAME_MAX_LENGTH,
      // Only surfaced once the user has actually typed, so the step doesn't
      // open already scolding them.
      touched: false,
    };
  },
  computed: {
    isLoading(): boolean {
      return this.$store.state.onBoarding.isLoading;
    },
    errorMessage(): string | null {
      return this.$store.state.onBoarding.errorMessage;
    },
    suggestions(): string[] {
      return this.$store.state.onBoarding.profileUsernameSuggestions;
    },
    validationError(): string | null {
      const code = validateUsername(this.username);
      if (code === null) return null;
      if (code === 'too_short') {
        return this.$t('on_boarding.profile_username_page.error_too_short', { min: USERNAME_MIN_LENGTH });
      }
      return this.$t('on_boarding.profile_username_page.error_blank');
    },
    inlineError(): string | null {
      // Shown once the user has typed — or straight away when the value we
      // prefilled is itself invalid, since Continue is disabled and silence
      // would leave no clue why.
      if (this.touched) return this.validationError;
      return this.username ? this.validationError : null;
    },
    canSubmit(): boolean {
      return this.validationError === null;
    },
  },
  mounted() {
    (this.$refs.usernameInput as HTMLInputElement | undefined)?.focus();
  },
  methods: {
    // Normalising on input means the field can never hold a value the
    // validator would then reject (spaces become dashes, "@" is dropped).
    //
    // The input is manually controlled (`:value` + `@input`), so when
    // normalisation strips the character just typed the model doesn't change,
    // Vue has no reason to re-render, and the DOM would keep showing text that
    // will never be saved — type "joey@" and the field reads "joey@" while the
    // stored value is "joey". Writing the normalised value back to the element
    // keeps what is displayed and what is saved in step.
    onInput(event: Event) {
      const element = event.target as HTMLInputElement;
      this.touched = true;
      const normalized = normalizeUsername(element.value);
      this.username = normalized;
      if (element.value !== normalized) element.value = normalized;
    },
    pick(suggestion: string) {
      this.username = suggestion;
      this.touched = true;
      this.$store.commit('onBoarding/setError', null);
    },
    regenerate() {
      const state = this.$store.state.onBoarding;
      const fullName = composeFullName(state.profileFirstName, state.profileLastName);
      const fresh = suggestUsernames(fullName, state.user?.email);
      this.$store.commit('onBoarding/setProfileUsernameSuggestions', fresh);
    },
    submit() {
      this.touched = true;
      if (!this.canSubmit) {
        this.$store.commit('onBoarding/setError', this.validationError);
        return;
      }
      this.$store.dispatch('onBoarding/submitProfileUsername', this.username);
    },
    backTapped() {
      this.$store.commit('onBoarding/setError', null);
      this.$store.commit('onBoarding/popPage');
    },
  },
  beforeUnmount() {
    this.$store.commit('onBoarding/setError', null);
  },
});
</script>
